import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

function CheckoutCancelled({ onBack }) {
  return (
    <>
      <XCircle size={54} weight="fill" className="text-[#D32F2F] mx-auto" />
      <h1 className="mt-6 font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="payment-cancel-title">Checkout cancelled</h1>
      <p className="mt-3 text-slate-600">No charge was made. You can try again anytime.</p>
      <Button className="mt-8 bg-[#0A2540]" onClick={onBack} data-testid="payment-back-pricing">Back to pricing</Button>
    </>
  );
}

function ConfirmingPayment() {
  return (
    <>
      <div className="mx-auto h-10 w-10 rounded-full border-2 border-slate-300 border-t-[#0A2540] animate-spin" />
      <h1 className="mt-6 font-[Chivo] text-3xl font-black text-[#0A2540]">Confirming your payment…</h1>
      <p className="mt-3 text-slate-600">This usually takes a few seconds.</p>
    </>
  );
}

function PaymentConfirmed({ packageName, amount, onGoDashboard }) {
  return (
    <>
      <CheckCircle size={54} weight="fill" className="text-emerald-600 mx-auto" />
      <h1 className="mt-6 font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="payment-success-title">Payment confirmed</h1>
      <p className="mt-3 text-slate-600">Thanks for choosing Taxfree Filer LLP. A preparer will reach out within 1 business day.</p>
      {packageName && (
        <div className="mt-6 rounded-md border tf-hairline bg-slate-50 p-4 text-left">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Package</span><span className="font-semibold text-[#0A2540]">{packageName}</span></div>
          <div className="flex justify-between text-sm mt-1"><span className="text-slate-500">Amount</span><span className="font-mono text-[#0A2540]">${Number(amount || 0).toFixed(2)}</span></div>
        </div>
      )}
      <Button className="mt-8 bg-[#0A2540]" onClick={onGoDashboard} data-testid="payment-go-dashboard">Go to Mission Control</Button>
    </>
  );
}

function PaymentPending({ onBack }) {
  return (
    <>
      <XCircle size={54} weight="fill" className="text-amber-500 mx-auto" />
      <h1 className="mt-6 font-[Chivo] text-3xl font-black text-[#0A2540]">Payment status pending</h1>
      <p className="mt-3 text-slate-600">We're still waiting on Stripe. Refresh in a moment or contact support.</p>
      <Button className="mt-8" variant="outline" onClick={onBack}>Back to pricing</Button>
    </>
  );
}

function SuccessContent({ state, navigate }) {
  if (state.loading) return <ConfirmingPayment />;
  if (state.status === "paid") {
    return <PaymentConfirmed packageName={state.package_name} amount={state.amount} onGoDashboard={() => navigate("/dashboard")} />;
  }
  return <PaymentPending onBack={() => navigate("/pricing")} />;
}

export default function PaymentResult({ success }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: success, status: null, package_name: "", amount: 0, tries: 0 });

  useEffect(() => {
    if (!success) return;
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    if (!sid) { setState({ loading: false, status: "unknown", package_name: "", amount: 0, tries: 0 }); return; }
    let attempts = 0;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        attempts += 1;
        if (data.payment_status === "paid" || attempts >= 8) {
          setState({ loading: false, status: data.payment_status, package_name: data.package_name, amount: data.amount, tries: attempts });
        } else {
          timer = setTimeout(poll, 2000);
        }
      } catch {
        if (!cancelled) setState({ loading: false, status: "error", package_name: "", amount: 0, tries: attempts });
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [location.search, success]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 grid place-items-center p-8">
        <div className="w-full max-w-lg rounded-lg border tf-hairline bg-white p-10 text-center" data-testid="payment-result">
          {success
            ? <SuccessContent state={state} navigate={navigate} />
            : <CheckoutCancelled onBack={() => navigate("/pricing")} />}
        </div>
      </div>
      <Footer />
    </div>
  );
}
